// src/pages/PremiumDashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import API from "../utils/api";
import FloatingNotice from "../components/FloatingNotice";
import ReferralWithdrawalCard from "../components/ReferralWithdrawalCard";
import { useCountry } from "../context/CountryContext";

import opayLogo from "../assets/opay.png";
import palmpayLogo from "../assets/palmpay.png";
import kudaLogo from "../assets/kuda.png";
import moniepointLogo from "../assets/moniepoint.png";
import binanceLogo from "../assets/binance.png";
import bybitLogo from "../assets/bybit.png";
import lunoLogo from "../assets/luno.png";
import paypalLogo from "../assets/paypal.png";

const TELEGRAM_BOT_URL = "https://t.me/probanksupport_bot";
const TAWK_SRC = "https://embed.tawk.to/68deec7532ab2019572628de/1j6jcnbu7";

const TelegramBotBanner = ({ theme, onDismiss }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-white/10 via-white/5 to-transparent border border-white/15 flex items-center justify-between gap-3 flex-wrap backdrop-blur-md"
  >
    <div className="flex items-start gap-3">
      <span className="text-2xl">🤖</span>
      <div>
        <p className="font-semibold text-white text-sm">Need help? Chat with our Telegram assistant</p>
        <p className="text-xs text-white/50 mt-0.5">
          Register, check your balance, buy coins, and get answers instantly.
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <a
        href={TELEGRAM_BOT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r ${theme.vendorBtn} text-black transition hover:brightness-110 shadow-lg whitespace-nowrap`}
      >
        Open Telegram Bot
      </a>
      <button onClick={onDismiss} className="text-white/40 hover:text-white text-lg leading-none px-1" aria-label="Dismiss">✕</button>
    </div>
  </motion.div>
);

const TawkFloatingButton = ({ theme }) => {
  const [statusMsg, setStatusMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const loadTawkAndOpen = () => {
    if (window.Tawk_API && typeof window.Tawk_API.maximize === "function") {
      window.Tawk_API.showWidget();
      window.Tawk_API.maximize();
      return;
    }
    if (document.getElementById("tawk-premium-script")) {
      setStatusMsg("Loading support chat…");
      setTimeout(() => {
        if (window.Tawk_API?.maximize) {
          window.Tawk_API.showWidget();
          window.Tawk_API.maximize();
          setStatusMsg("");
        } else {
          setStatusMsg("Still loading — try again in a moment.");
          setTimeout(() => setStatusMsg(""), 3000);
        }
      }, 1500);
      return;
    }

    setLoading(true);
    setStatusMsg("Loading support chat…");
    const s1 = document.createElement("script");
    s1.id = "tawk-premium-script";
    s1.async = true;
    s1.src = TAWK_SRC;
    s1.charset = "UTF-8";
    s1.setAttribute("crossorigin", "*");
    s1.onload = () => {
      let attempts = 0;
      const check = setInterval(() => {
        attempts++;
        if (window.Tawk_API?.maximize) {
          clearInterval(check);
          window.Tawk_API.showWidget();
          window.Tawk_API.maximize();
          setStatusMsg("");
          setLoading(false);
        } else if (attempts > 10) {
          clearInterval(check);
          setStatusMsg("Couldn't open support chat — try again.");
          setTimeout(() => setStatusMsg(""), 4000);
          setLoading(false);
        }
      }, 300);
    };
    s1.onerror = () => {
      setStatusMsg("Couldn't load support chat. Check your connection.");
      setTimeout(() => setStatusMsg(""), 4000);
      setLoading(false);
    };
    document.body.appendChild(s1);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {statusMsg && (
        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-black/90 border border-white/10 text-white text-xs rounded-lg whitespace-nowrap shadow-lg max-w-xs">
          {statusMsg}
        </div>
      )}
      <button
        onClick={loadTawkAndOpen}
        disabled={loading}
        className={`px-5 py-3 bg-gradient-to-r ${theme.supportBtn} hover:brightness-110 disabled:opacity-60 text-black font-bold rounded-full shadow-2xl flex items-center gap-2 transition transform active:scale-95`}
      >
        {loading ? "Loading…" : "Chat Support"}
        <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded">💬</span>
      </button>
    </div>
  );
};

export default function PremiumDashboard() {
  const { isGhana, config } = useCountry();

  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");
  const [telegramBannerVisible, setTelegramBannerVisible] = useState(true);
  // ✅ NEW: surfaces a real fetch failure instead of hanging on
  // "LOADING DASHBOARD" forever with no visible cause.
  const [loadError, setLoadError] = useState("");
  const navigate = useNavigate();

  const theme = isGhana
    ? {
        bgGradient: "from-[#2a0a00] via-[#1a0500] to-black",
        planBadge: "from-yellow-400 via-red-500 to-green-500",
        supportBtn: "from-yellow-400 via-red-500 to-green-500",
        copyBtn: "from-yellow-500 to-red-600",
        addMoneyBtn: "from-red-500 to-yellow-500",
        balanceGradient: "from-yellow-400 via-red-400 to-green-400",
        walletNameColor: "text-yellow-300",
        cryptoNameColor: "text-red-300",
        vendorAccent: "text-yellow-400",
        vendorBtn: "from-yellow-400 to-red-500",
        glowColor: "rgba(250,204,21,0.15)",
        currencySymbol: config.currencySymbol,
        appName: "ProBank Ghana",
        greeting: "Akwaaba",
        flag: "🇬🇭",
        referralDomain: "prorealalert.com",
        wallets: [
          { name: "MTN MoMo", emoji: "📱", color: "from-yellow-500 to-yellow-700", link: "/wallet", active: true },
          { name: "Telecel Cash", emoji: "💳", color: "from-red-500 to-red-700", link: "/wallet-palmpay", active: true },
          { name: "AirtelTigo", emoji: "💰", color: "from-blue-500 to-purple-600", link: "/wallet-kuda", active: true },
          { name: "GCB Bank", emoji: "🏦", color: "from-green-500 to-emerald-700", link: null, active: false },
        ],
      }
    : {
        bgGradient: "from-purple-950 via-indigo-950 to-black",
        planBadge: "from-yellow-400 via-pink-500 to-purple-500",
        supportBtn: "from-yellow-400 via-pink-500 to-purple-600",
        copyBtn: "from-pink-500 to-purple-600",
        addMoneyBtn: "from-yellow-500 to-pink-500",
        balanceGradient: "from-yellow-400 via-pink-500 to-purple-500",
        walletNameColor: "text-yellow-300",
        cryptoNameColor: "text-pink-400",
        vendorAccent: "text-yellow-400",
        vendorBtn: "from-yellow-400 to-pink-500",
        glowColor: "rgba(236,72,153,0.15)",
        currencySymbol: config.currencySymbol,
        appName: "ProBank",
        greeting: "Hello",
        flag: "🇳🇬",
        referralDomain: "prorealalert.com",
        wallets: [
          { name: "Opay", logo: opayLogo, link: "/wallet", active: true },
          { name: "PalmPay", logo: palmpayLogo, link: "/wallet-palmpay", active: true },
          { name: "Kuda", logo: kudaLogo, link: "/wallet-kuda", active: true },
          { name: "MoniePoint", logo: moniepointLogo, link: null, active: false },
        ],
      };

  useEffect(() => {
    const fetchData = () => {
      API.get("/dashboard")
        .then((res) => {
          setData(res.data);
          setLoadError("");
        })
        .catch((err) => {
          console.error(err);
          setLoadError(
            `${err.response?.status || "?"} ${err.response?.data?.message || err.message}`
          );
        });
    };
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(`https://${theme.referralDomain}/register?ref=${data?.user?.referralCode || ""}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const showComingSoon = (name) => {
    setNotice(`${name} coming soon!`);
    setTimeout(() => setNotice(""), 2000);
  };

  const handleWalletClick = (w) => {
    if (data.user?.isBalanceLocked) {
      setNotice("Account restricted");
      return;
    }
    w.active && w.link ? navigate(w.link) : showComingSoon(w.name);
  };

  if (!data) {
    return (
      <div className={`flex items-center justify-center min-h-screen bg-gradient-to-br ${theme.bgGradient} text-white p-6`}>
        <div className="text-center max-w-md">
          <p className="text-sm tracking-wide animate-pulse text-white/60 mb-4">LOADING DASHBOARD</p>
          {loadError && (
            <div className="mt-2 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-xs text-left break-words">
              🔬 Debug: {loadError}
            </div>
          )}
        </div>
      </div>
    );
  }

  const bankWallets = theme.wallets.map((w) => ({ ...w, balance: data.user?.balance || 0 }));

  const cryptoWallets = [
    { name: "Binance", logo: binanceLogo, active: false },
    { name: "Bybit", logo: bybitLogo, active: false },
    { name: "Luno", logo: lunoLogo, active: true, link: "/luno-dashboard" },
    { name: "PayPal", logo: paypalLogo, active: false },
  ];

  return (
    <div className={`min-h-screen p-6 md:p-10 bg-gradient-to-br ${theme.bgGradient} text-white relative overflow-hidden`}>
      <div className="pointer-events-none fixed -top-32 -right-32 w-[500px] h-[500px] rounded-full blur-[130px]" style={{ backgroundColor: theme.glowColor }} />
      <div className="pointer-events-none fixed -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-purple-500/10 blur-[130px]" />

      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 border border-white/10 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-white/70 font-medium">{config.name}</span>
      </div>

      <div className="max-w-6xl mx-auto relative">
        <header className="mb-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-3xl font-extrabold flex items-center gap-3 flex-wrap">
            {theme.greeting}, {data.user?.name || "User"}
            <span className={`px-3 py-1.5 text-xs bg-gradient-to-r ${theme.planBadge} text-black font-bold rounded-full shadow-lg uppercase tracking-wider`}>
              ✦ Premium Plan
            </span>
          </h2>
        </header>
        <p className="text-sm text-white/50 mb-6">Loan settlement, plus everything in Basic.</p>

        {telegramBannerVisible && <TelegramBotBanner theme={theme} onDismiss={() => setTelegramBannerVisible(false)} />}

        {data.user?.isBalanceLocked && (
          <div className="mb-4 p-4 rounded-2xl bg-red-600/20 border border-red-400/40 text-white shadow-lg backdrop-blur-sm">
            ⚠️ Your account is currently restricted
            <br />
            <span className="text-sm text-red-200">Reason: {data.user?.lockReason || "Contact support"}</span>
          </div>
        )}

        {data.user?.status === "rejected" && (
          <div className="mb-4 p-4 rounded-2xl bg-red-700/30 border border-red-500/40 text-white backdrop-blur-sm">
            ❌ Your account was rejected
            <br />
            <span className="text-sm">{data.user?.rejectionReason || "Contact support"}</span>
          </div>
        )}

        {data.user?.status === "pending" && (
          <div className="mb-4 p-4 rounded-2xl bg-yellow-500/20 border border-yellow-400/40 text-yellow-200 backdrop-blur-sm">
            ⏳ Your account is awaiting approval
          </div>
        )}

        {data.user?.systemMessage && (
          <div className="mb-4 p-4 rounded-2xl bg-blue-600/20 border border-blue-400/40 text-white backdrop-blur-sm">
            ℹ️ {data.user.systemMessage}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-3xl p-8 mb-6 overflow-hidden bg-gradient-to-br from-white/10 via-black/40 to-purple-900/30 border border-white/15 shadow-2xl"
        >
          <div className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl -mr-24 -mt-24" style={{ backgroundColor: theme.glowColor }} />
          <div className="relative">
            <p className="text-xs uppercase tracking-widest text-white/50 mb-2 font-semibold">Available Balance</p>
            <p className={`text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r ${theme.balanceGradient} mb-4`}>
              {theme.currencySymbol}{data.user?.balance?.toLocaleString() || "0"}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleCopy}
                className={`px-5 py-2.5 rounded-xl bg-gradient-to-r ${theme.copyBtn} font-semibold text-white shadow-lg hover:brightness-110 transition`}
              >
                {copied ? "✓ Copied Link" : "Copy Referral"}
              </button>
              <button
                onClick={() => {
                  if (data.user?.isBalanceLocked) { setNotice("Account restricted"); return; }
                  navigate("/purchase");
                }}
                className={`px-5 py-2.5 rounded-xl bg-gradient-to-r ${theme.addMoneyBtn} font-semibold text-black shadow-lg hover:brightness-110 transition`}
              >
                + Add Money
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 mb-6">
          <div className="md:col-span-2 p-6 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-md shadow-xl">
            <h3 className="font-semibold text-lg mb-1">{isGhana ? "Mobile Money & Banks" : "Wallets"}</h3>
            <p className="text-xs text-white/40 mb-5">Access your bank and crypto wallets</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {bankWallets.map((w, i) => (
                <motion.button
                  key={i}
                  whileHover={{ scale: 1.04 }}
                  onClick={() => handleWalletClick(w)}
                  className="p-4 rounded-2xl bg-black/30 border border-white/10 hover:border-white/25 flex flex-col items-center shadow-lg transition"
                >
                  <div className={`w-12 h-12 rounded-full ${w.color ? `bg-gradient-to-br ${w.color}` : "bg-white/5"} flex items-center justify-center overflow-hidden mb-2 shadow-inner`}>
                    {w.logo ? <img src={w.logo} alt={w.name} className="w-full h-full object-contain" /> : <span className="text-2xl">{w.emoji}</span>}
                  </div>
                  <div className={`text-xs font-bold ${theme.walletNameColor}`}>{w.name}</div>
                  <div className="text-xs text-white/40 mt-0.5">{theme.currencySymbol}{w.balance?.toLocaleString()}</div>
                </motion.button>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-white/10">
              <p className="text-xs font-semibold text-white/50 mb-3">Crypto Wallets</p>
              <div className="flex flex-wrap gap-2">
                {cryptoWallets.map((w, i) => (
                  <button
                    key={i}
                    onClick={() => handleWalletClick(w)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black/30 border border-white/10 hover:border-white/25 transition"
                  >
                    <img src={w.logo} alt={w.name} className="w-5 h-5" />
                    <span className={`text-xs font-semibold ${theme.cryptoNameColor}`}>{w.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar — Referrals + Withdrawal + Vendor */}
          <div className="space-y-5">
            <div className="p-5 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-black/30 border border-emerald-400/20 backdrop-blur-md shadow-xl">
              <h3 className="text-sm font-semibold text-white/70 mb-3">Referrals</h3>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div>
                  <p className="text-xs text-white/40 mb-0.5">Count</p>
                  {/* ✅ FIXED: was referralsCount (dead field, always 0) */}
                  <p className="font-bold text-white text-lg">{data.user?.referralCount || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">Bonus</p>
                  {/* ✅ FIXED: was referralBonus (dead field, always 0) */}
                  <p className="font-bold text-emerald-400 text-lg">{theme.currencySymbol}{data.user?.referralEarnings || 0}</p>
                </div>
              </div>
              <p className="text-xs text-pink-300 font-mono mb-3">{data.user?.referralCode || "N/A"}</p>
              <p className="text-[11px] text-white/30 break-all">
                https://{theme.referralDomain}/register?ref={data.user?.referralCode || ""}
              </p>
            </div>

            {/* ✅ NEW: was completely missing on Premium — same gap as Basic. */}
            <ReferralWithdrawalCard
              currencySymbol={theme.currencySymbol}
              theme={{
                card: "bg-white/[0.04] border border-white/10 backdrop-blur-md shadow-xl",
                accent: `bg-gradient-to-r ${theme.copyBtn} text-white`,
              }}
            />

            <div className="p-5 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-md shadow-xl">
              {data.user?.isVendor ? (
                <>
                  <p className="text-sm text-white/60 mb-1">Vendor</p>
                  <p className={`font-bold mb-3 ${theme.vendorAccent}`}>Your vendor dashboard is live</p>
                  <button
                    onClick={() => navigate("/vendor")}
                    className={`w-full py-2.5 rounded-xl bg-gradient-to-r ${theme.vendorBtn} text-black font-semibold shadow-lg hover:brightness-110 transition`}
                  >
                    Go to Vendor
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-white/60 mb-1">Become a Vendor</p>
                  <p className={`font-bold mb-3 ${theme.vendorAccent}`}>Start selling with {theme.appName}</p>
                  <button
                    onClick={() => navigate("/become-vendor")}
                    className={`w-full py-2.5 rounded-xl bg-gradient-to-r ${theme.vendorBtn} text-black font-semibold shadow-lg hover:brightness-110 transition`}
                  >
                    Apply Now
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {notice && <FloatingNotice message={notice} />}
      <TawkFloatingButton theme={theme} />
    </div>
  );
      }
